import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { authClient } from '../../lib/auth-client';
import Logo from '../../assets/images/logo.svg';

const schema = Yup.object({
  password: Yup.string().min(8, 'Minimum 8 characters').required('Password is required'),
  confirm: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
});

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const [showPassword, setShowPassword] = useState(false);
  const [done, setDone] = useState(false);

  const formik = useFormik({
    initialValues: { password: '', confirm: '' },
    validationSchema: schema,
    onSubmit: async (values, { setErrors, setSubmitting }) => {
      const { error } = await authClient.resetPassword({
        newPassword: values.password,
        token,
      });
      if (error) {
        setErrors({ password: error.message ?? 'Invalid or expired link' });
      } else {
        setDone(true);
        setTimeout(() => navigate('/login'), 2000);
      }
      setSubmitting(false);
    },
  });

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 2 }}>
      <Paper sx={{ p: 4, width: 400, borderRadius: '8px', boxShadow: '0 8px 32px rgba(15,23,42,0.10)', border: '1px solid #E2E8F0' }}>
        <Stack spacing={3} alignItems="center" mb={3}>
          <img src={Logo} alt="5milah" width={52} height={52} />
          <Stack spacing={0.5} alignItems="center">
            <Typography variant="h5" fontWeight={700}>Set your password</Typography>
            <Typography variant="body2" color="text.secondary">
              {done ? 'Password updated — redirecting…' : 'Choose a strong password for your account'}
            </Typography>
          </Stack>
        </Stack>

        {!done && (
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={2.5}>
              <Stack spacing={0.75}>
                <InputLabel htmlFor="rp-password" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                  New Password
                </InputLabel>
                <OutlinedInput
                  id="rp-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                  error={!!(formik.touched.password && formik.errors.password)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" size="large">
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
                {formik.touched.password && formik.errors.password && (
                  <FormHelperText error>{formik.errors.password}</FormHelperText>
                )}
              </Stack>

              <Stack spacing={0.75}>
                <InputLabel htmlFor="rp-confirm" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                  Confirm Password
                </InputLabel>
                <OutlinedInput
                  id="rp-confirm"
                  name="confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={formik.values.confirm}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                  error={!!(formik.touched.confirm && formik.errors.confirm)}
                />
                {formik.touched.confirm && formik.errors.confirm && (
                  <FormHelperText error>{formik.errors.confirm}</FormHelperText>
                )}
              </Stack>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={formik.isSubmitting || !token}
                sx={{ py: 1.25, fontWeight: 600, mt: 0.5 }}>
                {formik.isSubmitting ? 'Saving…' : 'Set Password'}
              </Button>

              {!token && (
                <FormHelperText error sx={{ textAlign: 'center' }}>
                  Invalid link. Please request a new one.
                </FormHelperText>
              )}
            </Stack>
          </form>
        )}
      </Paper>
    </Box>
  );
}

export default ResetPasswordPage;
